import type {
  TaskSignatureBypassResponse,
  TaskSignatureResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { COMPLETED_STATUSES } from '../../constants/statuses';

const DEFAULT_ROLE_LABELS: Record<string, string> = {
  clinician: 'clinician',
  case_manager: 'case manager',
};

const getRole = (sig: TaskSignatureResponse): string | undefined => {
  const meta = sig.signature_metadata as Record<string, unknown> | null;
  return meta?.signer_role as string | undefined;
};

const findByRole = (
  sigs: TaskSignatureResponse[],
  role: string
): TaskSignatureResponse | undefined => sigs.find(s => getRole(s) === role);

/**
 * The name to print for a signature's signer.
 *
 * `signer_name` is resolved server-side, which is what lets a chart signed by
 * someone who had no name on file show their name once an admin fills it in.
 * The metadata chain behind it is the fallback for a response served by an
 * older backend, and for the test fixtures that predate the field.
 *
 * Every surface that renders a signer goes through here so they cannot drift
 * apart again: two of them used to read `user_name` alone and so ignored
 * `signer_display_name` entirely.
 */
export const signerName = (sig: TaskSignatureResponse): string => {
  const meta = (sig.signature_metadata as Record<string, unknown> | null) ?? {};
  return (
    sig.signer_name ||
    (meta.signer_display_name as string) ||
    (meta.user_name as string) ||
    (meta.user_email as string) ||
    'Unknown signer'
  );
};

/** Who accepted a bypass. Same resolution contract as {@link signerName}. */
export const bypassActorName = (b: TaskSignatureBypassResponse): string => {
  const meta = (b.bypass_metadata as Record<string, unknown> | null) ?? {};
  return (
    b.actor_name ||
    (meta.actor_display_name as string) ||
    (meta.user_name as string) ||
    (meta.user_email as string) ||
    'Unknown user'
  );
};

interface SignedSigner {
  id: string;
  name: string;
  /** Display label for the signer's role (e.g. "case manager"). */
  roleLabel?: string;
  signedAt: string;
  /** The date the signer declared, when they declared one. */
  signatureDate?: string | null;
}

/** A clinician signature that was forfeited rather than collected (ROG-3269). */
export interface BypassNotice {
  id: string;
  /** Who performed the bypass — never the clinician who owed the signature. */
  actorName: string;
  /** Their credentials, e.g. "RN, BSN". Both PDFs print these; on the one
   *  record where the authorizer's standing is the point, the screen must not
   *  drop them. */
  actorCredentials?: string;
  reasonLabel: string;
  comments?: string;
  bypassedAt: string;
}

export interface SignatureSummaryData {
  /** Task is in a terminal status — treated as fully signed. */
  complete: boolean;
  signed: SignedSigner[];
  /** Human-readable awaiting labels, e.g. 'case manager', '1 signature'. */
  awaiting: string[];
  /**
   * Present when a clinician signature was bypassed. A bypassed chart is
   * complete but NOT fully signed, so every surface that renders this summary
   * must say so rather than showing an unqualified "Signed."
   */
  bypass?: BypassNotice;
}

const toBypassNotice = (b: TaskSignatureBypassResponse): BypassNotice => {
  const meta = (b.bypass_metadata as Record<string, unknown> | null) ?? {};
  return {
    id: b.id,
    actorName: bypassActorName(b),
    actorCredentials: (meta.actor_credentials as string) || undefined,
    reasonLabel: b.reason_label ?? b.reason,
    comments: b.comments ?? undefined,
    bypassedAt: b.bypassed_at,
  };
};

/**
 * Reduces a task's active signatures to a compact signed/awaiting summary.
 *
 * The branching mirrors the legacy SignatureStatusBadges pills (ROG-2553):
 * role-aware when `signer_role` metadata is present, count-based otherwise, and
 * a terminal/completed status is always treated as fully signed so single-signer
 * tasks don't falsely demand a second signature next to a "Completed" status.
 */
export function computeSignatureSummary(
  activeSignatures: TaskSignatureResponse[],
  status?: TaskStatus,
  bypasses: TaskSignatureBypassResponse[] = []
): SignatureSummaryData {
  // Only a bypass that still applies. Reopening a chart for editing supersedes
  // its bypass server-side, because the clinician signature is owed again — so
  // a superseded row is history, not a live forfeiture. Without this filter a
  // chart that was bypassed, reopened, and then genuinely signed renders
  // "clinician signature bypassed" beside the real signature that replaced it,
  // while the visits report correctly reads signature_bypassed = false.
  //
  // Newest first; a chart reopened and bypassed twice shows the latest reason.
  const latestBypass = bypasses
    .filter(b => !b.superseded_at)
    .sort((a, b) => b.bypassed_at.localeCompare(a.bypassed_at))[0];
  const bypass = latestBypass ? toBypassNotice(latestBypass) : undefined;
  const labelForRole = (role: string): string =>
    DEFAULT_ROLE_LABELS[role] ?? role.replace(/_/g, ' ');

  const toSigned = (sig: TaskSignatureResponse): SignedSigner => {
    const role = getRole(sig);
    return {
      id: sig.id,
      name: signerName(sig),
      roleLabel: role ? labelForRole(role) : undefined,
      signedAt: sig.signed_at,
      signatureDate: sig.signature_date,
    };
  };

  const clinicianSig = findByRole(activeSignatures, 'clinician');
  const caseManagerSig = findByRole(activeSignatures, 'case_manager');
  const unknownRoleSigs = activeSignatures.filter(s => !getRole(s));
  const total = Math.min(activeSignatures.length, 2);
  const isComplete = status ? COMPLETED_STATUSES.has(status) : false;

  if (isComplete) {
    return {
      complete: true,
      signed: activeSignatures.slice(0, 2).map(toSigned),
      // A bypassed chart is complete, so nothing is still "awaited" — the
      // bypass notice is what tells the reader the signature never came.
      awaiting: [],
      bypass,
    };
  }
  if (total === 0) {
    return { complete: false, signed: [], awaiting: ['2 signatures'], bypass };
  }
  if (total >= 2) {
    return {
      complete: false,
      signed: activeSignatures.slice(0, 2).map(toSigned),
      awaiting: [],
      bypass,
    };
  }
  if (clinicianSig) {
    return {
      complete: false,
      signed: [toSigned(clinicianSig)],
      awaiting: [labelForRole('case_manager')],
      bypass,
    };
  }
  if (caseManagerSig) {
    return {
      complete: false,
      signed: [toSigned(caseManagerSig)],
      // The clinician signature is only still owed if it was not forfeited.
      awaiting: bypass ? [] : [labelForRole('clinician')],
      bypass,
    };
  }
  const sig = unknownRoleSigs[0] ?? activeSignatures[0];
  return {
    complete: false,
    signed: [toSigned(sig)],
    awaiting: ['1 signature'],
    bypass,
  };
}
